import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Input from "../../../../../components/Input";
import TextArea from "../../../../../components/TextArea";
import Checkbox from "../../../../../components/Checkbox";
import Link from "../../../../../components/Link";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { formatAmount, formatDollarAmount } from "../../../../../utils/methods";
import { AdjustTransactionForm, TransactionListItem } from "../../../../../interfaces/managerStatement.interface";

interface AdjustTransactionModalContentProps {
  item: TransactionListItem | null;
  formData: AdjustTransactionForm;
  isAdminCaller: boolean;
  readOnly?: boolean;
  onInputChange: (e: any, name: string) => void;
  onResetToAuto: () => void;
}

const AdjustTransactionModalContent: React.FC<AdjustTransactionModalContentProps> = ({
  item, formData, isAdminCaller, readOnly, onInputChange, onResetToAuto,
}) => {
  const { t } = useTranslation();
  const base = 'reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.adjustModal';

  if (!item) return null;

  const paidToManagerAuto = Number(item.paid_to_manager_auto || 0);

  return (
    <div className="py-2">
      <div className="bg-body rounded p-3 mb-4">
        <div className="row g-3">
          <div className="col-4">
            <div className="text-heading text-uppercase small">{t(`${base}.summary.actualCost`)}</div>
            <div className="fw-semibold">{formatDollarAmount(formatAmount(Number(item.actual_amount || 0).toFixed(2)))}</div>
          </div>
          <div className="col-4">
            <div className="text-heading text-uppercase small">{t(`${base}.summary.margin`)}</div>
            <div className="fw-semibold">{formatDollarAmount(formatAmount(Number(item.margin_amount || 0).toFixed(2)))}</div>
          </div>
          <div className="col-4">
            <div className="text-heading text-uppercase small">{t(`${base}.summary.autoPaidToManager`)}</div>
            <div
              className={clsx('fw-semibold', {
                'text-success': paidToManagerAuto > 0,
                'text-danger': paidToManagerAuto < 0,
              })}
            >
              {formatDollarAmount(formatAmount(paidToManagerAuto.toFixed(2)))}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {isAdminCaller && (
          <div className="col-12 d-flex align-items-center gap-2">
            <Checkbox
              variant="primary"
              className="d-checkbox mb-0"
              labelClassName="fw-semibold"
              checked={formData.is_resolved}
              label={t(`${base}.fields.markAsResolved`)}
              name={VALIDATION_FIELD_MAPPING.markAsResolved}
              disabled={readOnly}
              onChange={onInputChange}
            />
            <span className="text-body small">— {t(`${base}.fields.adminOnly`)}</span>
          </div>
        )}
        <div className="col-12">
          <div className="mb-1 d-flex justify-content-between align-items-center">
            <div>
              <span className="fw-semibold">{t(`${base}.fields.paidToManagerOverride.label`)} ($)</span>
              <span className="text-body"> — {t(`${base}.fields.paidToManagerOverride.description`)}</span>
            </div>
            {isAdminCaller && !readOnly && (
              <Link
                text={t(`${base}.fields.paidToManagerOverride.resetToAuto`)}
                onClick={onResetToAuto}
                className="small"
              />
            )}
          </div>
          <Input
            type="number"
            id={VALIDATION_FIELD_MAPPING.paidToManagerOverride}
            name={VALIDATION_FIELD_MAPPING.paidToManagerOverride}
            label={t(`${base}.fields.paidToManagerOverride.label`)}
            labelClassName="visually-hidden"
            placeholder={t(`${base}.fields.paidToManagerOverride.placeholder`)}
            value={formData.paid_to_manager_override}
            disabled={readOnly || !isAdminCaller}
            onChange={onInputChange}
          />
        </div>

        <div className="col-12">
          <div className="mb-1">
            <span className="fw-semibold">{t(`${base}.fields.managerNote.label`)}</span>
            <span className="text-body"> — {t(`${base}.fields.managerNote.description`)}</span>
          </div>
          <TextArea
            id={VALIDATION_FIELD_MAPPING.managerNote}
            name={VALIDATION_FIELD_MAPPING.managerNote}
            label={t(`${base}.fields.managerNote.label`)}
            labelClassName="visually-hidden"
            placeholder={t(`${base}.fields.managerNote.placeholder`)}
            rows={3}
            value={formData.manager_note}
            disabled={readOnly}
            onChange={onInputChange}
          />
        </div>

        <div className="col-12">
          <div className="mb-1">
            <span className="fw-semibold">{t(`${base}.fields.adminNote.label`)}</span>
            <span className="text-body"> — {t(`${base}.fields.adminNote.description`)}</span>
          </div>
          <TextArea
            id={VALIDATION_FIELD_MAPPING.adminNote}
            name={VALIDATION_FIELD_MAPPING.adminNote}
            label={t(`${base}.fields.adminNote.label`)}
            labelClassName="visually-hidden"
            placeholder={t(`${base}.fields.adminNote.placeholder`)}
            rows={3}
            value={formData.admin_note}
            disabled={readOnly || !isAdminCaller}
            onChange={onInputChange}
          />
        </div>
      </div>
    </div>
  );
};

export default AdjustTransactionModalContent;
